import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { type User, type UserRole, type UserStatus, type UserFilters, DEFAULT_PERMISSIONS } from '@/types/user';
import { UsersTable } from '@/components/users/users-table';
import { ViewUserDialog } from '@/components/users/view-user-dialog';
import { DeleteUserDialog } from '@/components/users/delete-user-dialog';
import { ManageUserIntegrationsDialog } from '@/components/users/manage-user-integrations-dialog';
import { ManageUserCompaniesDialog } from '@/components/users/manage-user-companies-dialog';
import { useUsersList, useDeleteUser, useUpdateUser } from '@/hooks/useUsersData';
import { useApi } from '@/hooks/useApi';

import { Search } from 'lucide-react';
import { toast } from 'sonner';

// ⚡ VERSION 3.0.0 - ROLE EDITING ENABLED ⚡
console.log('🚀 UserManagementPage v3.0.0 with Role Editing loaded!');

export function UserManagementPage() {
  // Fetch users using mode-aware query (demo mode uses mock data, live mode uses API)
  const { data: fetchedUsers } = useUsersList();
  const deleteUserMutation = useDeleteUser();
  const updateUserMutation = useUpdateUser();
  const { apiPost } = useApi();

  // Local state for users (initialized from fetched data, can be modified locally for edits)
  const [users, setUsers] = useState<User[]>([]);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: 'all',
    status: 'all',
    sortBy: 'name_asc',
  });

  // Update local users state when fetched data changes
  useEffect(() => {
    if (fetchedUsers) {
      setUsers(fetchedUsers);
    }
  }, [fetchedUsers]);

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [manageIntegrationsDialogOpen, setManageIntegrationsDialogOpen] = useState(false);
  const [manageCompaniesDialogOpen, setManageCompaniesDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName.toLowerCase().includes(searchLower) ||
          user.lastName.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
      );
    }

    // Role filter
    if (filters.role !== 'all') {
      filtered = filtered.filter((user) => user.role === filters.role);
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter((user) => user.status === filters.status);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'name_asc':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'name_desc':
          return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
        case 'recent':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'role':
          return a.role.localeCompare(b.role);
        default:
          return 0;
      }
    });

    return filtered;
  }, [users, filters]);

  const handleDeleteUser = (userId: string, reassignToUserId?: string) => {
    deleteUserMutation.mutate(userId, {
      onSuccess: () => {
        // Remove the user from local state for immediate UI update
        setUsers(prevUsers => prevUsers.filter((user) => user.id !== userId));
        if (reassignToUserId) {
          toast.success('User deleted and accounts reassigned successfully');
        } else {
          toast.success('User deleted successfully');
        }
      },
      onError: (error) => {
        toast.error(`Failed to delete user: ${error.message}`);
      },
    });
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleDeleteUserClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleManageIntegrations = (user: User) => {
    setSelectedUser(user);
    setManageIntegrationsDialogOpen(true);
  };

  const handleManageCompanies = (user: User) => {
    setSelectedUser(user);
    setManageCompaniesDialogOpen(true);
  };

  const handleSaveIntegration = async (userId: string, evoUserId: string, externalFirstName: string, externalLastName: string) => {
    try {
      // Call the API to persist the integration
      await apiPost(`/api/v1/user/${userId}`, {
        evoUserId: evoUserId || null,
        externalFirstName: externalFirstName || null,
        externalLastName: externalLastName || null,
      });

      // Update local state on success
      setUsers(
        users.map((user) =>
          user.id === userId
            ? { 
                ...user, 
                evoUserId: evoUserId || undefined,
                externalFirstName: externalFirstName || undefined,
                externalLastName: externalLastName || undefined,
                updatedAt: new Date().toISOString() 
              }
            : user
        )
      );
      toast.success('User integration updated successfully');
    } catch (error) {
      console.error('Error saving user integration:', error);
      toast.error('Failed to save user integration');
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: UserRole) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    try {
      // Update the user with new role and default permissions for that role
      await updateUserMutation.mutateAsync({
        userId,
        data: {
          ...user,
          role: newRole,
          permissions: DEFAULT_PERMISSIONS[newRole],
        },
      });

      // Update local state for immediate UI feedback
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId
            ? { ...u, role: newRole, permissions: DEFAULT_PERMISSIONS[newRole], updatedAt: new Date().toISOString() }
            : u
        )
      );

      toast.success(`User role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
      throw error; // Re-throw to let the table component handle loading state
    }
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground mt-2">
            View, edit, and manage user accounts across your organization. Users are automatically created through the Evo system integration.
          </p>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9"
            />
          </div>

          {/* Role Filter */}
          <Select
            value={filters.role}
            onValueChange={(value) =>
              setFilters({ ...filters, role: value as UserRole | 'all' })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="administrator">Administrator</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select
            value={filters.status}
            onValueChange={(value) =>
              setFilters({ ...filters, status: value as UserStatus | 'all' })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {/* Sort */}
          <Select
            value={filters.sortBy}
            onValueChange={(value) =>
              setFilters({
                ...filters,
                sortBy: value as UserFilters['sortBy'],
              })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name A-Z</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="role">Role</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </div>

        {/* Users Table */}
        <UsersTable
          users={filteredUsers}
          onViewUser={handleViewUser}
          onDeleteUser={handleDeleteUserClick}
          onManageIntegrations={handleManageIntegrations}
          onManageCompanies={handleManageCompanies}
          onUpdateUserRole={handleUpdateUserRole}
          canManageUsers={true}
        />

        {/* Dialogs */}
        <ViewUserDialog
          open={viewDialogOpen}
          onOpenChange={setViewDialogOpen}
          user={selectedUser}
        />

        <DeleteUserDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          user={selectedUser}
          users={users}
          onDeleteUser={handleDeleteUser}
        />

        <ManageUserIntegrationsDialog
          open={manageIntegrationsDialogOpen}
          onOpenChange={setManageIntegrationsDialogOpen}
          user={selectedUser}
          onSave={handleSaveIntegration}
        />

        <ManageUserCompaniesDialog
          open={manageCompaniesDialogOpen}
          onOpenChange={setManageCompaniesDialogOpen}
          user={selectedUser}
        />
      </div>
    </div>
  );
}
